import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import LeaderboardCard from "@/components/LeaderboardCard";
import { Award, Send, CheckCircle2, Clock3, ShieldCheck, Trophy } from "lucide-react";

const PRE_PRIMARY_CLASSES = ["Play Group", "LKG", "UKG"];
const PP_COLUMNS = [
  { key: "FA1", label: "Formative Assessment-1" },
  { key: "FA2", label: "Formative Assessment-2" },
  { key: "MIDTERM", label: "Mid Term Exam" },
  { key: "FA3", label: "Formative Assessment-3" },
  { key: "FA4", label: "Formative Assessment-4" },
  { key: "ANNUAL", label: "Annual Exam" },
];
const MT_TO_FA = { MT1: "FA1", MT2: "FA2", SEM1: "MIDTERM", MT3: "FA3", MT4: "FA4", SEM2: "ANNUAL" };

export default function ClassResults() {
  const { user } = useAuth();
  const sections = useMemo(() => user?.class_teacher_of || [], [user]);
  const isReadOnly = user?.role === "super_admin";

  const [columns, setColumns] = useState([]);
  const [examKey, setExamKey] = useState("");
  const [ppCurrent, setPpCurrent] = useState("");
  const [sel, setSel] = useState(sections[0] ? `${sections[0].class_name}|${sections[0].section}` : "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/assessments/meta").then(({ data }) => {
      setColumns(data.columns || []);
      setExamKey(data.current_key || data.columns?.[0]?.key || "");
      setPpCurrent(MT_TO_FA[data.current_key] || "");
    }).catch(() => {});
  }, []);

  const [classSel, secSel] = sel ? sel.split("|") : ["", ""];
  const isPP = PRE_PRIMARY_CLASSES.includes(classSel);
  const activeColumns = isPP ? PP_COLUMNS : columns;

  // Keep the exam selector valid when switching between regular and pre-primary.
  useEffect(() => {
    const keys = (isPP ? PP_COLUMNS : columns).map((c) => c.key);
    if (!keys.length) return;
    if (isPP) {
      if (!keys.includes(examKey)) setExamKey(ppCurrent && keys.includes(ppCurrent) ? ppCurrent : keys[0]);
    } else if (!keys.includes(examKey)) {
      setExamKey(keys[0]);
    }
    // eslint-disable-next-line
  }, [sel, columns]);

  const load = async () => {
    if (!classSel || !examKey) return;
    setLoading(true);
    try {
      const url = isPP ? "/assessments/pre-primary/class-results" : "/class-results";
      const { data } = await api.get(url, { params: { class_name: classSel, section: secSel, exam_key: examKey } });
      setData(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load class results");
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (classSel && examKey) load(); /* eslint-disable-next-line */ }, [sel, examKey]);

  const submitToHM = async () => {
    setSubmitting(true);
    try {
      const url = isPP ? "/assessments/pre-primary/class-results/submit-to-hm" : "/class-results/submit-to-hm";
      await api.post(url, { class_name: classSel, section: secSel, exam_key: examKey });
      toast.success("Result submitted to HM for publishing.");
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not submit to HM");
    } finally { setSubmitting(false); }
  };

  if (sections.length === 0) {
    return (
      <Layout>
        <div className="py-20 text-center text-slate-500" data-testid="class-results-not-ct">
          You are not assigned as a class teacher, so there are no class results to consolidate.
        </div>
      </Layout>
    );
  }

  const submitted = data?.class_status === "submitted_to_hm" || data?.class_status === "submitted";
  const published = data?.class_status === "published";

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Award className="h-6 w-6 text-indigo-500" /> Class Results
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Consolidated marks for your class. Subjects appear here once their teacher submits.</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1.5">Class</div>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="h-11 w-44" data-testid="cr-class-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sections.map((p) => (
                <SelectItem key={`${p.class_name}|${p.section}`} value={`${p.class_name}|${p.section}`}>Class {p.class_name}-{p.section}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1.5">Test / Exam</div>
          <Select value={examKey} onValueChange={setExamKey}>
            <SelectTrigger className="h-11 w-60" data-testid="cr-exam-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {activeColumns.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Class leaderboard — same view students see, shown above the marks list */}
      {!isPP && classSel && secSel && (
        <div className="mb-8" data-testid="cr-leaderboard">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2 mb-4" style={{ fontFamily: "Outfit" }}>
            <Trophy className="h-5 w-5 text-amber-500" /> Class Leaderboard — Class {classSel}-{secSel}
          </h2>
          <LeaderboardCard endpoint="/teacher/leaderboard" params={{ class_name: classSel, section: secSel, exam_key: examKey }} />
        </div>
      )}

      {/* Subject submission status */}
      {data && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="cr-subject-status">
          {data.all_subjects.map((s) => {
            const done = data.submitted_subjects.includes(s);
            return (
              <span key={s} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${done ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`} data-testid={`cr-subj-${done ? "done" : "pending"}`}>
                {done ? <CheckCircle2 className="h-3 w-3" /> : <Clock3 className="h-3 w-3" />} {s}
              </span>
            );
          })}
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : !data ? (
          <div className="p-10 text-center text-slate-500">Select a class & test.</div>
        ) : data.submitted_subjects.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="cr-empty">No subjects have been submitted yet for this test.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-4 py-3 sticky left-0 bg-slate-50 dark:bg-slate-900/40 z-10 w-44">Roll · Student</th>
                  {data.submitted_subjects.map((s) => <th key={s} className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">{s}</th>)}
                  {!isPP && <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wide text-indigo-600">Total</th>}
                  {!isPP && <th className="text-center px-3 py-3 text-xs font-semibold uppercase tracking-wide text-indigo-600">%</th>}
                </tr>
              </thead>
              <tbody>
                {data.students.map((st, idx) => (
                  <tr key={st.student_id} className={`border-b border-slate-100 dark:border-slate-800 ${idx % 2 ? "bg-slate-50/40 dark:bg-slate-900/20" : ""}`} data-testid={`cr-row-${st.student_id}`}>
                    <td className="px-4 py-2 sticky left-0 bg-inherit z-10">
                      <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{st.name}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">{st.roll_number}</div>
                    </td>
                    {data.submitted_subjects.map((s) => {
                      const c = st.subjects[s];
                      if (isPP) {
                        const kind = data.subject_kinds?.[s] || "subject";
                        let v = "—", weak = false;
                        if (!c) v = "—";
                        else if (c.absent) v = "AB";
                        else if (kind === "activity") v = c.grade || "—";
                        else {
                          v = c.marks == null ? "—" : `${c.marks}/${c.max ?? data.max ?? ""}`;
                          weak = c.marks != null && (c.max || data.max) > 0 && (c.marks / (c.max || data.max)) * 100 < 40;
                        }
                        return <td key={s} className={`text-center px-3 py-2 ${c?.absent ? "text-rose-600 font-semibold" : weak ? "text-rose-500 font-semibold" : "text-slate-700 dark:text-slate-200"}`} data-testid={`cr-cell-${st.student_id}-${s}`}>{v}</td>;
                      }
                      const v = !c ? "—" : c.absent ? "AB" : (c.marks == null ? "—" : `${c.marks}/${c.max_marks}`);
                      const subjWeak = c && !c.absent && c.marks != null && c.max_marks > 0 && (c.marks / c.max_marks) * 100 < 50;
                      return <td key={s} className={`text-center px-3 py-2 ${c?.absent ? "text-rose-600 font-semibold" : subjWeak ? "text-rose-500 font-semibold" : "text-slate-700 dark:text-slate-200"}`}>{v}</td>;
                    })}
                    {!isPP && <td className="text-center px-3 py-2 font-semibold text-slate-900 dark:text-slate-100">{st.total}/{st.max_total}</td>}
                    {!isPP && <td className={`text-center px-3 py-2 font-semibold ${st.percentage != null && st.percentage < 50 ? "text-rose-500" : "text-indigo-600"}`}>{st.percentage != null ? `${st.percentage}%` : "—"}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Submit to HM */}
      {data && (
        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="cr-hm-bar">
          {published ? (
            <span className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 px-4 py-2.5 text-sm font-semibold"><ShieldCheck className="h-4 w-4" /> Published by admin</span>
          ) : submitted ? (
            <span className="inline-flex items-center gap-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 px-4 py-2.5 text-sm font-semibold" data-testid="cr-submitted-hm"><CheckCircle2 className="h-4 w-4" /> Submitted to HM — awaiting publish</span>
          ) : (
            <Button
              onClick={submitToHM}
              disabled={!data.all_submitted || submitting || isReadOnly}
              title={data.all_submitted ? "Send to HM for publishing" : `Pending subjects: ${data.pending_subjects.join(", ") || "none"}`}
              className="h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
              data-testid="cr-submit-hm-btn"
            >
              <Send className="h-4 w-4 mr-1.5" /> {submitting ? "Submitting…" : "Submit to HM"}
            </Button>
          )}
          {!data.all_submitted && !submitted && !published && (
            <span className="text-xs text-slate-500">Enabled once all {data.all_subjects.length} subjects are submitted ({data.submitted_subjects.length} done).</span>
          )}
        </div>
      )}
    </Layout>
  );
}
